import { type BillingInvoiceDto, type BillingOverviewDto, type BillingSubscriptionDto } from '../../services/billing.api';
import { type AccountMembersOverviewDto, type ProfileDto } from '../../services/profile.api';
import { i18nService, t } from '../../services/i18n';

export type ProfileTab = 'profile' | 'subscription' | 'invoices';

export type BillingPlanDetail = {
  descriptionKey: string;
  features: string[];
  featured?: boolean;
};

export const BILLING_PLAN_DETAILS: Record<string, BillingPlanDetail> = {
  starter: {
    descriptionKey: 'pricing.starter.desc',
    features: [
      'pricing.feature.remindersTimeline',
      'pricing.feature.dashboard',
    ],
  },
  pro: {
    descriptionKey: 'pricing.pro.desc',
    features: [
      'pricing.feature.remindersTimeline',
      'pricing.feature.prioritySupport',
    ],
    featured: true,
  },
  business: {
    descriptionKey: 'pricing.business.desc',
    features: [
      'pricing.feature.remindersTimeline',
      'pricing.feature.dedicatedSupport',
    ],
  },
};

export type ProfileViewState = {
  activeTab: ProfileTab;
  profile: ProfileDto | null;
  accountMembers: AccountMembersOverviewDto | null;
  billingOverview: BillingOverviewDto | null;
  isBillingSyncing: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isPortalLoading: boolean;
  checkoutLoadingKey: string;
  subscriptionLoadingKey: string;
  isCancellingSubscription: boolean;
  error: string;
  billingError: string;
  billingTermsError: string;
  billingTermsAccepted: boolean;
  userName: string;
  userEmail: string;
  providerLabel: string;
  isAdmin: boolean;
  canManageAccount: boolean;
  allowedTabs: ProfileTab[];
  restrictedAccess: boolean;
  isSwitchingAccountMode: boolean;
  accountModeError: string;
  isManagingAccountMembers: boolean;
  accountMembersError: string;
};

export type BillingCancellationNoticeKind = 'trial_started' | 'profile_only';

export function esc(str: string): string {
  const el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}

export function formatDate(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
}

export function formatMoney(amountInCents: number, currency: string): string {
  return new Intl.NumberFormat(i18nService.dateLocale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountInCents / 100);
}

export function intervalLabel(interval?: string | null): string {
  return interval === 'yearly'
    ? t('pricing.toggle.yearly')
    : interval === 'monthly'
      ? t('pricing.toggle.monthly')
      : '-';
}

export function subscriptionStatusLabel(status?: string | null): string {
  const lookup: Record<string, string> = {
    active: t('billing.status.active'),
    trialing: t('billing.status.trialing'),
    past_due: t('billing.status.pastDue'),
    canceled: t('billing.status.canceled'),
    unpaid: t('billing.status.unpaid'),
    incomplete: t('billing.status.incomplete'),
    incomplete_expired: t('billing.status.incompleteExpired'),
    pending: t('billing.status.pending'),
    processing: t('billing.status.pending'),
    failed: t('billing.status.failed'),
    expired: t('billing.status.expired'),
    paid: t('billing.status.paid'),
    open: t('billing.status.open'),
    draft: t('billing.status.draft'),
    void: t('billing.status.void'),
  };

  if (!status) {
    return '-';
  }

  return lookup[status] || status;
}

export function initials(name: string): string {
  return name.split(' ').filter(Boolean).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
}

export function formatCents(cents: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

export function formatBytes(bytes?: number | null): string {
  if (bytes == null || Number.isNaN(bytes)) {
    return '-';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = Math.max(0, bytes);
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const decimals = value >= 10 || unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[unitIndex]}`;
}

export function formatApproxDocumentCount(storageLimitBytes: number): string {
  const roundedStorageInGb = Math.max(1, Math.round(storageLimitBytes / (1024 * 1024 * 1024)));
  const estimatedCount = roundedStorageInGb * 500;
  return new Intl.NumberFormat(i18nService.dateLocale).format(estimatedCount);
}

export function formatPropertyLimit(limit: number): string {
  return limit === 1
    ? t('billing.propertyLimitSingular')
    : t('billing.propertyLimitPlural', { count: limit });
}

export function getTrialDaysRemaining(overview: BillingOverviewDto | null): number {
  const end = overview?.trialEndsAtUtc;
  if (!end) {
    return 0;
  }

  const endDate = new Date(end);
  if (Number.isNaN(endDate.getTime())) {
    return 0;
  }

  const diffMs = endDate.getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
}

export function isTrialSubscription(
  subscription: BillingSubscriptionDto | null | undefined,
  overview: BillingOverviewDto | null,
): boolean {
  return subscription?.status === 'trialing' && !overview?.trialExpired;
}

export function renderSubscriptionField(label: string, value: string): string {
  return `
    <div class="billing-info-row">
      <span class="billing-info-label">${esc(label)}</span>
      <strong class="billing-info-value">${esc(value)}</strong>
    </div>
  `;
}

export function renderInvoice(invoice: BillingInvoiceDto): string {
  const invoiceTitle = invoice.number || t('billing.invoiceFallbackTitle');
  const invoiceKind = isCreditNote(invoice) ? t('billing.creditNoteLabel') : t('billing.invoiceLabel');
  const invoiceLink = invoice.invoicePdfUrl || invoice.hostedInvoiceUrl;
  const isInternalInvoiceLink = !!invoiceLink && invoiceLink.startsWith('/');
  return `
    <article class="billing-invoice-item">
      <div>
        <strong>${esc(invoiceTitle)}</strong>
        <div class="billing-invoice-meta">${esc(invoiceKind)} • ${formatDate(invoice.createdUtc)} • ${subscriptionStatusLabel(invoice.status)}</div>
      </div>
      <div class="billing-invoice-side">
        <strong>${formatMoney(invoice.totalInCents, invoice.currency)}</strong>
        ${!invoiceLink
          ? ''
          : isInternalInvoiceLink
            ? `<button type="button" class="btn btn-link" data-action="download-invoice" data-url="${esc(invoiceLink)}" data-file-name="${esc(`${invoiceTitle}.pdf`)}">${t('billing.viewInvoice')}</button>`
            : `<a href="${esc(invoiceLink)}" target="_blank" rel="noopener">${t('billing.viewInvoice')}</a>`}
      </div>
    </article>
  `;
}

function isCreditNote(invoice: BillingInvoiceDto): boolean {
  return Boolean(invoice.number && /^CN-/i.test(invoice.number));
}
